import React, { useState, useEffect, useCallback } from "react";
import { useSpiRTCContext } from "../../components/spi-rtc";
import { Caixa } from "../../models/caixa";
import { useApi } from "../../components/api";
import {
  Backdrop,
  CircularProgress,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { CaixaWidget } from "./CaixaWidget";
import Grid from "@material-ui/core/Grid";

const useStyles = makeStyles((theme) => ({
  loadingBackdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
    backgroundColor: "rgba(0, 0, 0, 0.35)",
  },
  dateBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 0",
  },
  dateButton: {
    cursor: "pointer",
    padding: "2px 10px",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

interface CaixasSelector {
  [id: string]: Caixa;
}

export const CaixasPage = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<CaixasSelector>({});
  const [dataRef, setDataRef] = useState(new Date());
  const [pickerOpen, setPickerOpen] = useState(false);

  const { hub } = useSpiRTCContext();
  const { api } = useApi();

  const recarregar = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = dataRef.toLocaleDateString("pt-BR");
      const str = data.split("/").reverse().join("-");
      const resp = await api.get<{ caixas: CaixasSelector }>("caixas/" + str);
      setStatus(resp.data.caixas ?? {});
    } catch (error) {}
    setIsLoading(false);
  }, [api, dataRef]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  useEffect(() => {
    if (hub) {
      hub.on("vendaTotalizada", () => recarregar());
    }
    return () => {
      hub?.off("vendaTotalizada");
    };
  }, [hub, recarregar]);

  const isToday =
    dataRef.toLocaleDateString("pt-BR") ===
    new Date().toLocaleDateString("pt-BR");

  const handleMinus = () => {
    const novo = new Date(dataRef.getTime() - 24 * 60 * 60 * 1000);
    setDataRef(novo);
  };

  const handlePlus = () => {
    if (!isToday) {
      const novo = new Date(dataRef.getTime() + 24 * 60 * 60 * 1000);
      setDataRef(novo);
    }
  };

  const lookupCaixas = Object.keys(status)
    .filter((x) => x)
    .map((x) => status[x]);

  return (
    <div>
      <Backdrop className={classes.loadingBackdrop} open={isLoading}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>
      <Grid container spacing={0}>
        <Grid item xs={12}>
          <div className={classes.dateBar}>
            <IconButton size="small" onClick={handleMinus}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="subtitle1"
              className={classes.dateButton}
              onClick={() => setPickerOpen(true)}
            >
              <b>{dataRef.toLocaleDateString("pt-BR")}</b>
            </Typography>
            <KeyboardDatePicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              value={dataRef}
              onChange={(date) => date && setDataRef(date.toDate())}
              format="DD/MM/YYYY"
              maxDate={new Date()}
              TextFieldComponent={() => <span />}
            />
            <IconButton size="small" onClick={handlePlus} disabled={isToday}>
              <ChevronRightIcon />
            </IconButton>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            spacing={4}
            justifyContent="center"
            style={{ padding: "18px 0px" }}
          >
            {lookupCaixas.map((c) => (
              <Grid key={`${c.turno}_${c.operadorNome}`} item xs={12} md={6}>
                <CaixaWidget caixa={c} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
